import type { BillingTranslator } from '@/lib/billing/billing-translator'
import { BILLING_PLANS } from '@/lib/billing/plans'

/** The self-serve subscription changes that need a confirmation step. */
export type PlanChangeKind =
  | 'upgrade'
  | 'downgrade'
  | 'cancel'
  | 'resume'
  | 'keep'

/** One line of the confirm's money summary (upgrades). */
export type PlanChangeSummaryRow = {
  label: string
  value: string
  isStrong?: boolean
}

type PlanChangeConfirmCopy = {
  title: string
  body: string
  /** What the change costs and when, shown under the copy. */
  summary?: ReadonlyArray<PlanChangeSummaryRow>
  confirmLabel: string
  confirmVariant: 'default' | 'destructive'
  /** Unset: the dialog's own Cancel (`useConfirm`'s default). */
  cancelLabel?: string
}

type PlanChangeConfirmArgs = {
  kind: PlanChangeKind
  currentPlanName: string
  targetPlanName: string
  /** Target price label (e.g. "$99/month"). Shown on upgrades. */
  targetPriceLabel?: string | null
  /** Human date the change takes effect (e.g. "July 4, 2026"). */
  effectiveDateLabel?: string | null
}

/** "Free: 500 credits and 1,000 sends a month", from the plan table. */
function freePlanAllowance(): {
  freePlan: string
  credits: number
  sends: number
} {
  const free = BILLING_PLANS.free
  return {
    freePlan: free.name,
    credits: free.credits,
    sends: free.emailSends,
  }
}

/**
 * Copy for the plan-change confirmation step the plan grid raises through the
 * app-wide `useConfirm` dialog, in the reader's language
 * (`billing.planChange`). Money-affecting actions (upgrade charges now,
 * cancel ends the plan) always pass through it; the dialog holds itself open
 * with both buttons disabled while the change settles, so a slow Stripe call
 * cannot be double-submitted.
 */
export function getPlanChangeConfirmCopy(
  t: BillingTranslator,
  args: PlanChangeConfirmArgs
): PlanChangeConfirmCopy {
  const { currentPlanName, targetPlanName, targetPriceLabel } = args
  const date = args.effectiveDateLabel

  switch (args.kind) {
    case 'upgrade': {
      return {
        title: t('planChange.upgrade.title', { plan: targetPlanName }),
        body: t('planChange.upgrade.body', { plan: targetPlanName }),
        ...(targetPriceLabel
          ? {
              summary: [
                {
                  label: t('planChange.upgrade.summary.today'),
                  value: t('planChange.upgrade.summary.prorated'),
                  isStrong: true,
                },
                {
                  label: t('planChange.upgrade.summary.then'),
                  value: date
                    ? t('planChange.upgrade.summary.renewal', {
                        price: targetPriceLabel,
                        date,
                      })
                    : targetPriceLabel,
                },
              ],
            }
          : {}),
        confirmLabel: t('planChange.upgrade.confirm', { plan: targetPlanName }),
        confirmVariant: 'default',
      }
    }
    case 'downgrade': {
      return {
        title: t('planChange.downgrade.title', { plan: targetPlanName }),
        body: date
          ? t('planChange.downgrade.bodyUntil', {
              current: currentPlanName,
              target: targetPlanName,
              date,
            })
          : t('planChange.downgrade.body', {
              current: currentPlanName,
              target: targetPlanName,
            }),
        confirmLabel: t('planChange.downgrade.confirm', {
          plan: targetPlanName,
        }),
        confirmVariant: 'default',
        cancelLabel: t('planChange.keepCurrentPlan'),
      }
    }
    case 'cancel': {
      return {
        title: t('planChange.cancel.title', { plan: currentPlanName }),
        body: date
          ? t('planChange.cancel.bodyAllowanceUntil', {
              plan: currentPlanName,
              date,
              ...freePlanAllowance(),
            })
          : t('planChange.cancel.bodyAllowance', {
              plan: currentPlanName,
              ...freePlanAllowance(),
            }),
        confirmLabel: t('planChange.cancel.confirmPlan', {
          plan: currentPlanName,
        }),
        confirmVariant: 'destructive',
        cancelLabel: t('planChange.keep.confirm', { plan: currentPlanName }),
      }
    }
    case 'resume': {
      return {
        title: t('planChange.resume.title', { plan: currentPlanName }),
        body: t('planChange.resume.body', { plan: currentPlanName }),
        confirmLabel: t('planChange.resume.confirm'),
        confirmVariant: 'default',
        cancelLabel: t('planChange.neverMind'),
      }
    }
    case 'keep': {
      return {
        title: t('planChange.keep.title', { plan: currentPlanName }),
        body: t('planChange.keep.body', { plan: currentPlanName }),
        confirmLabel: t('planChange.keep.confirm', { plan: currentPlanName }),
        confirmVariant: 'default',
        cancelLabel: t('planChange.neverMind'),
      }
    }
  }
}
